/**
 * Mamm Gift Engine — Native Free-Gift Logic (Phase 2: Multi-Goodie + Choice)
 * ============================================================================
 *
 * Liest Promotion-Konfiguration aus <script type="application/json"
 * data-mamm-gift-engine>, watcht Cart-State, managed:
 *   - Per Goodie: Eligibility (Tier-Threshold pro Goodie)
 *   - Per Goodie: Auto-Add ODER Manual-Click via Plus-Button
 *   - Targeting (Tags, Logged-In, Countries, Markets)
 *   - Progress-Bar (1- oder 2-Tier)
 *   - Goodies-Section unter Cart-Items (Locked-State sichtbar = Discovery)
 *
 * Cart-Item-Properties:
 *   _mamm_gift: 'true'              — Item ist ein Goodie
 *   _mamm_promotion_id: <section.id> — verweist auf Promotion
 *   _mamm_goodie_id: <block.id>      — eindeutige Goodie-Referenz
 *
 * Discount-Mechanik:
 *   Pro Goodie-Produkt muss im Shopify Admin ein Automatic Discount aktiv
 *   sein (Buy X get Y, 100% off bei Threshold-Erfüllung).
 *
 * Public API (window.MammGiftEngine):
 *   .sync()            — manuelle Re-Synchronisation
 *   .state             — aktueller Engine-State (read-only)
 *   .clearRemoved()    — Reset aller "user-removed"-Flags
 *   .version           — '2.0.0'
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────
  //  Constants
  // ─────────────────────────────────────────────────────────
  const STORAGE_KEY_REMOVED = 'mamm-gift-removed';
  const PROPERTY_GIFT = '_mamm_gift';
  const PROPERTY_PROMOTION_ID = '_mamm_promotion_id';
  const PROPERTY_GOODIE_ID = '_mamm_goodie_id';
  const CART_FETCH_DEBOUNCE_MS = 250;
  const FLAG = '__mammGiftEngineV2__';

  if (window[FLAG]) return;
  window[FLAG] = true;

  // ─────────────────────────────────────────────────────────
  //  Read configuration
  // ─────────────────────────────────────────────────────────
  function readJSON(selector) {
    try {
      const el = document.querySelector(selector);
      if (!el) return null;
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      console.warn('[mamm-gift-engine] JSON parse failed:', selector, e);
      return null;
    }
  }

  const config = readJSON('script[data-mamm-gift-engine]');
  const customerData = readJSON('script[data-mamm-gift-customer]');

  if (!config || !Array.isArray(config.promotions) || config.promotions.length === 0) {
    return;
  }

  // ─────────────────────────────────────────────────────────
  //  State
  // ─────────────────────────────────────────────────────────
  const state = {
    cart: null,
    promotions: config.promotions,
    labels: config.labels || {},
    customer: customerData || {},
    removedKeys: new Set(),
    syncInProgress: false,
    lastSyncAt: 0,
    // Achievement-Animation State — in JS gehalten, nicht im DOM-Dataset.
    // Pebble's morphSection ersetzt das DOM und würde Dataset-Werte loeschen,
    // sodass Animation bei jedem Re-Render erneut triggert (Spring-Effekt).
    lastGiftReached: false,
  };

  try {
    const stored = localStorage.getItem(STORAGE_KEY_REMOVED);
    if (stored) {
      const arr = JSON.parse(stored);
      if (Array.isArray(arr)) arr.forEach((k) => state.removedKeys.add(String(k)));
    }
  } catch (e) {}

  function persistRemoved() {
    try {
      localStorage.setItem(STORAGE_KEY_REMOVED, JSON.stringify(Array.from(state.removedKeys)));
    } catch (e) {}
  }

  function goodieKey(promotion, goodie) {
    return String(promotion.id) + ':' + String(goodie.id);
  }

  // ─────────────────────────────────────────────────────────
  //  Helpers
  // ─────────────────────────────────────────────────────────
  function debounce(fn, ms) {
    let t;
    return function () {
      const args = arguments;
      const ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms);
    };
  }

  function formatMoney(cents, currencyIso) {
    const value = (Number(cents) || 0) / 100;
    const cur = (currencyIso || 'CHF').toUpperCase();
    const symbolMap = { CHF: 'CHF', EUR: '€', USD: '$', GBP: '£' };
    const symbol = symbolMap[cur] || cur;

    let formatted;
    try {
      formatted = value.toLocaleString(cur === 'CHF' ? 'de-CH' : 'de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch (e) {
      formatted = value.toFixed(2);
    }
    return symbol + ' ' + formatted;
  }

  function interpolate(template, vars) {
    let result = String(template || '');
    Object.keys(vars || {}).forEach(function (key) {
      result = result.replace(new RegExp('\\{' + key + '\\}', 'g'), vars[key]);
    });
    return result;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"]/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch];
    });
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/'/g, '&#39;');
  }

  // ─────────────────────────────────────────────────────────
  //  Cart-API
  // ─────────────────────────────────────────────────────────
  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('cart fetch HTTP ' + res.status);
      return await res.json();
    } catch (e) {
      console.warn('[mamm-gift-engine] fetchCart failed:', e);
      return null;
    }
  }

  // Sammelt alle aktiven cart-items-component sectionIds — für die Section-Render-API.
  // Pebble's cart-drawer/items-component setzt data-section-id auf den Section-Container.
  function getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      const id = el.getAttribute('data-section-id');
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }

  // Dispatcht Pebble's CartUpdateEvent-Format: bubbles cart:update, detail.data.sections
  // → cart-items-component fängt es und ruft morphSection — DOM updated ohne Page-Reload.
  function dispatchCartUpdate(sections, itemCount) {
    const evt = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: null,
        sourceId: '',
        data: {
          sections: sections || {},
          itemCount: typeof itemCount === 'number' ? itemCount : undefined,
          isIncremental: false,
          source: 'mamm-gift-engine',
        },
      },
    });
    document.dispatchEvent(evt);
  }

  async function cartAdd(variantId, quantity, properties) {
    const sectionIds = getCartSectionIds();
    const body = {
      items: [{ id: Number(variantId), quantity: quantity, properties: properties }],
    };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error('cart add HTTP ' + res.status + ' :: ' + txt.slice(0, 200));
    }
    return await res.json();
  }

  async function cartChange(itemKey, quantity) {
    const sectionIds = getCartSectionIds();
    const body = {
      id: itemKey,
      quantity: quantity,
    };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error('cart change HTTP ' + res.status + ' :: ' + txt.slice(0, 200));
    }
    return await res.json();
  }

  // ─────────────────────────────────────────────────────────
  //  Targeting
  // ─────────────────────────────────────────────────────────
  function targetingMatches(promotion) {
    const t = promotion.targeting || {};
    const c = state.customer || {};

    if (t.logged_in_only && !c.logged_in) return false;

    if (Array.isArray(t.customer_tags) && t.customer_tags.length > 0) {
      const customerTagsRaw = c.tags || '';
      const customerTags = String(customerTagsRaw)
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      const required = t.customer_tags.map((s) => String(s).toLowerCase());
      const hasMatch = required.some((tag) => customerTags.indexOf(tag) >= 0);
      if (!hasMatch) return false;
    }

    if (Array.isArray(t.countries) && t.countries.length > 0) {
      const current = String(c.country || '').toUpperCase();
      const allowed = t.countries.map((s) => String(s).toUpperCase());
      if (allowed.indexOf(current) < 0) return false;
    }

    if (Array.isArray(t.markets) && t.markets.length > 0) {
      const current = String(c.market || '').toLowerCase();
      const allowed = t.markets.map((s) => String(s).toLowerCase());
      if (allowed.indexOf(current) < 0) return false;
    }

    return true;
  }

  // ─────────────────────────────────────────────────────────
  //  Threshold logic
  // ─────────────────────────────────────────────────────────
  function getCurrentCurrency() {
    return String(state.customer.currency || 'CHF').toLowerCase();
  }

  function getShippingThresholdCents(promotion) {
    if (!promotion.shipping_tier || !promotion.shipping_tier.enabled) return 0;
    const fieldName = 'threshold_' + getCurrentCurrency();
    let value = promotion.shipping_tier[fieldName];
    if (value == null) value = promotion.shipping_tier.threshold_chf;
    return Math.round(Number(value || 0) * 100);
  }

  // Sammelt alle Tiers (Goodies + Versand) sortiert nach Schwelle aufsteigend.
  // Jeder Tier ist ein Achievement-Milestone in der Progress-Bar.
  // tier = { type: 'goodie'|'shipping', cents, label, productTitle, goodie? }
  function getAllTiers(promotion) {
    const tiers = [];

    const shippingCents = getShippingThresholdCents(promotion);
    if (shippingCents > 0) {
      tiers.push({
        type: 'shipping',
        cents: shippingCents,
        label: (state.labels && state.labels.shipping_dot_label) || 'Versand',
        productTitle: 'gratis Versand',
      });
    }

    if (Array.isArray(promotion.goodies)) {
      promotion.goodies.forEach(function (g) {
        const cents = getGoodieThresholdCents(g);
        if (cents > 0) {
          tiers.push({
            type: 'goodie',
            cents: cents,
            label: (state.labels && state.labels.gift_dot_label) || 'Geschenk',
            productTitle: g.product_title || 'dein Geschenk',
            goodie: g,
          });
        }
      });
    }

    tiers.sort(function (a, b) { return a.cents - b.cents; });
    return tiers;
  }

  // Maximale Schwelle = Bar-100%-Punkt. Bei 0 Tiers: 0.
  function getMaxThresholdCents(promotion) {
    const tiers = getAllTiers(promotion);
    if (tiers.length === 0) return 0;
    return tiers[tiers.length - 1].cents;
  }

  // Multi-Tier ist aktiv ab 2+ Tiers (egal ob 2 Goodies oder Goodie+Shipping
  // oder mehrere Goodies + Shipping). Dann werden Milestone-Dots gezeigt.
  function isMultiTierActive(promotion) {
    return getAllTiers(promotion).length >= 2;
  }

  function getGoodieThresholdCents(goodie) {
    const cur = getCurrentCurrency();
    let value = goodie.thresholds && goodie.thresholds[cur];
    if (value == null) value = goodie.thresholds && goodie.thresholds.chf;
    return Math.round(Number(value || 0) * 100);
  }

  function getCartTotalEligible(cart) {
    // Identisch zur Pebble-Cart-Drawer-„Zwischensumme" (cart.items_subtotal_price),
    // abzüglich der Goodie-Line-Prices. items_subtotal_price = Σ item.line_price
    // (= original_line_price minus line-level discounts). So matched die Bar
    // exakt das, was der User unten in der Cart-Summary sieht.
    if (!cart) return 0;
    let total = Number(cart.items_subtotal_price || 0);
    if (Array.isArray(cart.items)) {
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        if (item.properties && item.properties[PROPERTY_GIFT] === 'true') {
          total -= Number(item.line_price || 0);
        }
      }
    }
    return Math.max(0, total);
  }

  function isGoodieEligible(goodie, cart, promotion) {
    if (!targetingMatches(promotion)) return false;
    const threshold = getGoodieThresholdCents(goodie);
    const total = getCartTotalEligible(cart);
    return total >= threshold;
  }

  // ─────────────────────────────────────────────────────────
  //  Cart-Item lookups
  // ─────────────────────────────────────────────────────────
  function findGoodieLine(cart, goodie, promotion) {
    if (!cart || !Array.isArray(cart.items)) return null;
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];

      // Primary: Property-Match (Mamm-Engine Auto-Add mit allen 3 Markern)
      if (
        item.properties &&
        item.properties[PROPERTY_GIFT] === 'true' &&
        String(item.properties[PROPERTY_PROMOTION_ID]) === String(promotion.id) &&
        String(item.properties[PROPERTY_GOODIE_ID]) === String(goodie.id)
      ) {
        return item;
      }

      // Fallback: Variant-ID-Match — robuster gegen Property-Mismatch oder
      // wenn Goodie-Variante anders ins Cart gelangt ist (z.B. Shopify-
      // BXGY-Discount der Y-Item auto-addet ohne unsere Custom-Properties).
      // Defense gegen Goodie-Section-Plus-Button-Loophole bei doppeltem Add.
      if (
        goodie.variant_id &&
        item.variant_id &&
        String(item.variant_id) === String(goodie.variant_id)
      ) {
        return item;
      }
    }
    return null;
  }

  function getAllGiftKeys(cart) {
    // Map<itemKey, { promotionId, goodieId }>
    const map = {};
    if (!cart || !Array.isArray(cart.items)) return map;
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (item.properties && item.properties[PROPERTY_GIFT] === 'true') {
        map[item.key] = {
          promotionId: String(item.properties[PROPERTY_PROMOTION_ID] || ''),
          goodieId: String(item.properties[PROPERTY_GOODIE_ID] || ''),
        };
      }
    }
    return map;
  }

  // ─────────────────────────────────────────────────────────
  //  Sync — main brain
  // ─────────────────────────────────────────────────────────
  async function addGoodie(promotion, goodie) {
    if (!goodie.variant_id) {
      console.warn('[mamm-gift-engine] no variant_id', goodie.id);
      return null;
    }
    const props = {};
    props[PROPERTY_GIFT] = 'true';
    props[PROPERTY_PROMOTION_ID] = String(promotion.id);
    props[PROPERTY_GOODIE_ID] = String(goodie.id);
    return await cartAdd(goodie.variant_id, 1, props); // { items, sections }
  }

  async function sync() {
    if (state.syncInProgress) return;
    state.syncInProgress = true;
    try {
      let cart = await fetchCart();
      if (!cart) return;
      state.cart = cart;

      let lastSections = null;

      for (let i = 0; i < state.promotions.length; i++) {
        const promotion = state.promotions[i];
        const goodies = Array.isArray(promotion.goodies) ? promotion.goodies : [];
        const targetingOk = targetingMatches(promotion);

        for (let j = 0; j < goodies.length; j++) {
          const goodie = goodies[j];
          const line = findGoodieLine(cart, goodie, promotion);
          const key = goodieKey(promotion, goodie);
          const userRemoved = state.removedKeys.has(key);

          if (!targetingOk) {
            if (line) {
              try {
                const r = await cartChange(line.key, 0);
                if (r && r.sections) lastSections = r.sections;
                cart = state.cart = await fetchCart();
              } catch (e) {
                console.warn('[mamm-gift-engine] removal (targeting) failed:', e);
              }
            }
            continue;
          }

          const eligible = isGoodieEligible(goodie, cart, promotion);

          if (eligible) {
            if (line) continue;
            if (userRemoved && !promotion.behavior?.reapply_after_remove) continue;
            if (userRemoved && promotion.behavior?.reapply_after_remove) {
              state.removedKeys.delete(key);
              persistRemoved();
            }
            if (goodie.auto_add) {
              try {
                const r = await addGoodie(promotion, goodie);
                if (r && r.sections) lastSections = r.sections;
                cart = state.cart = await fetchCart();
              } catch (e) {
                console.warn('[mamm-gift-engine] add failed:', goodie.id, e);
              }
            }
            // !auto_add → User klickt + manuell
          } else {
            if (line) {
              try {
                const r = await cartChange(line.key, 0);
                if (r && r.sections) lastSections = r.sections;
                cart = state.cart = await fetchCart();
              } catch (e) {
                console.warn('[mamm-gift-engine] removal (ineligible) failed:', e);
              }
            }
            if (userRemoved) {
              state.removedKeys.delete(key);
              persistRemoved();
            }
          }
        }
      }

      // Wenn sync irgendwas geändert hat: Pebble's morphSection triggern
      if (lastSections) {
        dispatchCartUpdate(lastSections, state.cart ? state.cart.item_count : undefined);
      }

      decorateCartItems();
      updateProgressBar();
      renderGoodiesSection();
      state.lastSyncAt = Date.now();
    } finally {
      state.syncInProgress = false;
    }
  }

  // ─────────────────────────────────────────────────────────
  //  Cart-Item Decoration
  // ─────────────────────────────────────────────────────────
  function decorateCartItems() {
    if (!state.cart) return;
    const giftKeys = getAllGiftKeys(state.cart);

    document.querySelectorAll('tr[data-key]').forEach(function (row) {
      const key = row.getAttribute('data-key');
      const meta = giftKeys[key];
      const isGift = !!meta;
      row.classList.toggle('mamm-gift-line', isGift);

      if (isGift) {
        ensureGiftBadge(row);
        ensureGiftRemoveHandler(row, meta.promotionId, meta.goodieId);
      } else {
        const existing = row.querySelector('.mamm-gift-badge');
        if (existing) existing.remove();
      }
    });
  }

  function ensureGiftBadge(row) {
    if (row.querySelector('.mamm-gift-badge')) return;
    const titleEl = row.querySelector('.cart-items__title');
    const detailsCell = row.querySelector('.cart-items__details .cart-items__product-info') ||
                        row.querySelector('.cart-items__details');
    if (!detailsCell) return;
    const badge = document.createElement('span');
    badge.className = 'mamm-gift-badge';
    badge.textContent = state.labels.gift_badge_label || 'Geschenk von Mamm';
    if (titleEl && titleEl.parentNode === detailsCell) {
      detailsCell.insertBefore(badge, titleEl);
    } else {
      detailsCell.insertBefore(badge, detailsCell.firstChild);
    }
  }

  function ensureGiftRemoveHandler(row, promotionId, goodieId) {
    if (row.dataset.mammGiftHooked === '1') return;
    row.dataset.mammGiftHooked = '1';

    const onRemoveClick = function () {
      if (!promotionId || !goodieId) return;
      state.removedKeys.add(String(promotionId) + ':' + String(goodieId));
      persistRemoved();
    };

    // Mamm-Layout (M21) rendert .cart-items__remove-button ZWEIMAL pro Row:
    //   1× in cart-items__quantity (CSS-hidden via display:none — fallback)
    //   2× in cart-items__remove (sichtbar — der ✕ den User klickt)
    // querySelector würde nur die erste (hidden) finden → Listener nie getriggert.
    // querySelectorAll attach to ALL → defensiv robust gegen Markup-Variationen.
    const removeBtns = row.querySelectorAll('.cart-items__remove-button');
    removeBtns.forEach(function (btn) {
      btn.addEventListener('click', onRemoveClick, true);
    });
  }

  // ─────────────────────────────────────────────────────────
  //  Progress Bar
  // ─────────────────────────────────────────────────────────
  function updateProgressBar() {
    const progressEl = document.querySelector('[data-mamm-gift-progress]');
    if (!progressEl || !state.cart) return;

    let promotion = null;
    for (let i = 0; i < state.promotions.length; i++) {
      if (targetingMatches(state.promotions[i])) {
        promotion = state.promotions[i];
        break;
      }
    }

    if (!promotion) {
      progressEl.setAttribute('hidden', '');
      document.body.removeAttribute('data-mamm-gift-active');
      document.body.removeAttribute('data-mamm-gift-2tier');
      return;
    }
    progressEl.removeAttribute('hidden');
    document.body.setAttribute('data-mamm-gift-active', 'true');

    const tiers = getAllTiers(promotion);
    const maxCents = tiers.length > 0 ? tiers[tiers.length - 1].cents : 0;
    const total = getCartTotalEligible(state.cart);
    const allAchieved = maxCents > 0 && total >= maxCents;
    const multiTier = tiers.length >= 2;
    const currency = state.customer.currency || 'CHF';

    // Bar fillt sich proportional bis zur HÖCHSTEN Tier-Schwelle (= ultimate goal)
    const fillPct = maxCents > 0
      ? Math.min(100, Math.max(0, (total / maxCents) * 100))
      : 0;

    progressEl.classList.toggle('mamm-gift-progress--unlocked', allAchieved);
    progressEl.classList.toggle('mamm-gift-progress--has-milestones', multiTier);

    const fill = progressEl.querySelector('[data-mamm-gift-fill]');
    if (fill) fill.style.width = fillPct + '%';

    // ─── Smart Message basierend auf Tier-State ───
    // - All achieved: unlocked_label
    // - Next tier ist shipping: shipping_progress_label
    // - Next tier ist goodie + bereits etwas erreicht: between_progress_label
    // - Next tier ist goodie + nichts erreicht: progress_label
    const message = progressEl.querySelector('[data-mamm-gift-message]');
    if (message) {
      const labels = state.labels || {};
      let html = '';

      if (tiers.length === 0) {
        html = '';
      } else if (allAchieved) {
        // Letztes erreichtes Tier als "{gift}" für unlocked_label
        const lastTier = tiers[tiers.length - 1];
        html = interpolate(
          labels.unlocked_label || 'Geschenk freigeschaltet — du erhältst <strong>{gift}</strong> gratis.',
          { gift: lastTier.productTitle }
        );
      } else {
        // Find next un-achieved tier
        let nextTier = null;
        for (let i = 0; i < tiers.length; i++) {
          if (total < tiers[i].cents) { nextTier = tiers[i]; break; }
        }
        const remaining = nextTier ? formatMoney(nextTier.cents - total, currency) : '';
        // Find last achieved tier (für between-state)
        let lastAchieved = null;
        for (let i = tiers.length - 1; i >= 0; i--) {
          if (total >= tiers[i].cents) { lastAchieved = tiers[i]; break; }
        }

        if (lastAchieved && nextTier) {
          // Between-State: irgendwas wurde schon erreicht, mehr verfügbar.
          // Funktioniert sowohl wenn next ein Goodie ist als auch wenn next
          // shipping ist — Achievement-Hint wird via {achieved} sichtbar.
          html = interpolate(
            labels.between_progress_label || '<strong>{achieved}</strong> freigeschaltet — noch <strong>{remaining}</strong> für <strong>{gift}</strong> gratis.',
            { remaining: remaining, gift: nextTier.productTitle, achieved: lastAchieved.productTitle }
          );
        } else if (nextTier && nextTier.type === 'shipping') {
          // Pre-First mit Shipping als erstem Tier
          html = interpolate(
            labels.shipping_progress_label || 'Noch <strong>{remaining}</strong> für <strong>gratis Versand</strong>.',
            { remaining: remaining, gift: nextTier.productTitle, achieved: '' }
          );
        } else if (nextTier) {
          // Pre-First mit Goodie als erstem Tier
          html = interpolate(
            labels.progress_label || 'Noch <strong>{remaining}</strong> für <strong>{gift}</strong> gratis.',
            { remaining: remaining, gift: nextTier.productTitle, achieved: '' }
          );
        }
      }
      const tipp = goodiesFuerKarte(promotion).length > 0;
      if (tipp) html = mitTipp(html);
      if (message.innerHTML !== html) message.innerHTML = html;
      if (tipp) {
        if (!message.hasAttribute('data-mw-gs-tipp')) {
          message.setAttribute('data-mw-gs-tipp', '');
          message.setAttribute('role', 'button');
          message.setAttribute('tabindex', '0');
        }
      } else if (message.hasAttribute('data-mw-gs-tipp')) {
        message.removeAttribute('data-mw-gs-tipp');
        message.removeAttribute('role');
        message.removeAttribute('tabindex');
      }
    }

    // ─── Icon-State ───
    // shipping  → next-tier ist shipping (Truck-Icon)
    // gift      → next-tier ist goodie ODER nur 1 Tier (Gift-Icon)
    // achieved  → alle Tiers erreicht (Gift-Icon, scaled + drop-shadow)
    const visual = progressEl.querySelector('[data-mamm-gift-visual]');
    if (visual) {
      let iconState = 'gift';
      if (allAchieved) {
        iconState = 'achieved';
      } else {
        let nextTier = null;
        for (let i = 0; i < tiers.length; i++) {
          if (total < tiers[i].cents) { nextTier = tiers[i]; break; }
        }
        if (nextTier && nextTier.type === 'shipping') iconState = 'shipping';
      }
      if (visual.getAttribute('data-mamm-gift-icon-state') !== iconState) {
        visual.setAttribute('data-mamm-gift-icon-state', iconState);
      }
    }

    // ─── Dynamische Milestones-Render (n Tiers) ───
    renderMilestones(progressEl, tiers, total, maxCents);

    // ─── Achievement-Animation: nur beim Übergang allAchieved false → true ───
    if (allAchieved && !state.lastGiftReached) {
      progressEl.classList.remove('mamm-gift-progress--just-unlocked');
      // eslint-disable-next-line no-unused-expressions
      progressEl.offsetWidth;
      progressEl.classList.add('mamm-gift-progress--just-unlocked');
      window.setTimeout(function () {
        progressEl.classList.remove('mamm-gift-progress--just-unlocked');
      }, 1200);
    }
    state.lastGiftReached = allAchieved;
  }

  // Render-Logic für n Milestone-Dots auf der Bar.
  // Position proportional zu maxCents. Letztes Tier sitzt auf 100% (right edge).
  // Achievement-Class wenn cart >= tier.cents.
  function renderMilestones(progressEl, tiers, total, maxCents) {
    const container = progressEl.querySelector('[data-mamm-gift-milestones]');
    if (!container) return;

    if (tiers.length < 2 || maxCents <= 0) {
      if (!container.hasAttribute('hidden')) container.setAttribute('hidden', '');
      document.body.removeAttribute('data-mamm-gift-2tier');
      return;
    }
    if (container.hasAttribute('hidden')) container.removeAttribute('hidden');
    document.body.setAttribute('data-mamm-gift-2tier', 'true');

    const newHtml = tiers.map(function (tier, i) {
      const pct = (tier.cents / maxCents) * 100;
      const achieved = total >= tier.cents;
      const isLast = i === tiers.length - 1;
      const classes = [
        'mamm-gift-progress__milestone',
        'mamm-gift-progress__milestone--' + tier.type,
      ];
      if (isLast) classes.push('mamm-gift-progress__milestone--last');
      if (achieved) classes.push('mamm-gift-progress__milestone--achieved');

      return (
        '<div class="' + classes.join(' ') + '" style="left: ' + pct + '%" data-tier-type="' + escapeAttr(tier.type) + '">' +
        '<div class="mamm-gift-progress__milestone-dot">' +
        '<svg class="mamm-gift-progress__milestone-check" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">' +
        '<path d="M2.5 6.5L5 9L9.5 3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '</div>' +
        '<div class="mamm-gift-progress__milestone-label">' + escapeHtml(tier.label) + '</div>' +
        '</div>'
      );
    }).join('');

    if (container.innerHTML !== newHtml) container.innerHTML = newHtml;
  }

  // ─────────────────────────────────────────────────────────
  //  Geschenk-Karte (Entscheid Albinot 17.10.2026, Tafel «Geschenk mit
  //  eigenem Platz»): eigene Karte unter den Artikeln. Sichtbar ab gratis
  //  Versand (gesperrt mit Restbetrag) oder sobald ein Geschenk erreicht ist,
  //  nie für Geschenke, die schon im Warenkorb liegen.
  // ─────────────────────────────────────────────────────────
  function goodiesFuerKarte(promotion) {
    if (!promotion || !Array.isArray(promotion.goodies) || !state.cart) return [];
    const versand = getShippingThresholdCents(promotion);
    const versandErreicht = versand > 0 && getCartTotalEligible(state.cart) >= versand;
    return promotion.goodies.filter(function (g) {
      if (findGoodieLine(state.cart, g, promotion)) return false;
      return isGoodieEligible(g, state.cart, promotion) || versandErreicht;
    });
  }

  // Balken: der Teil nach « · » wird zum Tipp-Ziel, das zur Geschenk-Karte
  // scrollt (ohne « · » der ganze Satz). Nur solange die Karte sichtbar ist.
  const TIPP_PFEIL =
    '<svg class="mw-gs-tipp__pfeil" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
  function mitTipp(html) {
    if (!html) return html;
    const trenner = ' · ';
    const pos = html.indexOf(trenner);
    if (pos === -1) return '<span class="mw-gs-tipp">' + html + TIPP_PFEIL + '</span>';
    return (
      html.slice(0, pos + trenner.length) +
      '<span class="mw-gs-tipp">' + html.slice(pos + trenner.length) + TIPP_PFEIL + '</span>'
    );
  }

  // ─────────────────────────────────────────────────────────
  //  Goodies-Section (unter Cart-Items)
  // ─────────────────────────────────────────────────────────
  function renderGoodiesSection() {
    // Karte «Für dich» (S4/S10b, 16.10.2026): Die Engine füllt nur noch die
    // Geschenk-Karte(n) in [data-mamm-goodies-list]. Die Karte selbst bleibt
    // stehen, weil dahinter die Empfehlungen aus mamm-warenkorb.js folgen.
    document.querySelectorAll('[data-mamm-gift-goodies]').forEach(function (container) {
      renderGoodiesInto(container);
    });
  }

  function renderGoodiesInto(container) {
    if (!container || !state.cart) return;
    const listEl = container.querySelector('[data-mamm-goodies-list]');
    if (!listEl) return;

    let promotion = null;
    for (let i = 0; i < state.promotions.length; i++) {
      if (targetingMatches(state.promotions[i])) {
        promotion = state.promotions[i];
        break;
      }
    }

    // Ab gratis Versand (gesperrt) oder ab der Stufe (frei), Entscheid 17.10.2026
    const goodiesNotInCart = goodiesFuerKarte(promotion);
    const titelEl = container.querySelector('[data-mw-gs-titel]');
    if (titelEl) {
      const frei = goodiesNotInCart.some(function (g) { return isGoodieEligible(g, state.cart, promotion); });
      const lt = container.dataset || {};
      const titel = frei ? (lt.lTitelFrei || 'Dein Geschenk ist freigeschaltet') : (lt.lTitelZu || 'Schalte dein Geschenk frei');
      if (titelEl.textContent !== titel) titelEl.textContent = titel;
    }

    const newHtml = goodiesNotInCart
      .map(function (goodie) {
        return renderGoodieItem(goodie, promotion, container);
      })
      .join('');
    // DIFF-CHECK: nur ersetzen, wenn sich etwas ändert (kein Springen)
    if (listEl.innerHTML !== newHtml) {
      listEl.innerHTML = newHtml;
      listEl.querySelectorAll('[data-mamm-goodie-add]').forEach(function (btn) {
        btn.addEventListener('click', onGoodieAddClick);
      });
      container.dispatchEvent(new CustomEvent('mamm:fuer-dich', { bubbles: true }));
    }
    if (newHtml) container.setAttribute('data-gift', '');
    else container.removeAttribute('data-gift');
  }

  function renderGoodieItem(goodie, promotion, container) {
    const eligible = isGoodieEligible(goodie, state.cart, promotion);
    const l = (container && container.dataset) || {};
    const currency = state.customer.currency || 'CHF';
    const title = String(goodie.product_title || '')
      .replace(/^AntiDirt\s+/i, '')
      .replace(/\bPremium\s+/g, '')
      .trim();
    const titleEsc = escapeHtml(title);
    const imgSrc = goodie.product_image || '';
    const threshold = getGoodieThresholdCents(goodie);
    const key = escapeAttr(goodieKey(promotion, goodie));
    const imgHtml = imgSrc
      ? '<img src="' + escapeAttr(imgSrc) + '" alt="" width="64" height="64" loading="lazy">'
      : '';

    if (!eligible) {
      // Gesperrt: Bild blass mit Schloss, kleiner Balken, Restbetrag
      // Analyse B6 (Albinot 17.10.2026): kein zweiter Balken in der Karte, der Balken oben zeigt den Fortschritt
      // Analyse A3 (Albinot 17.10.2026): Restbetrag steht schon im Balken, hier nur die Schwelle
      const teile = String(l.lNoch || 'Ab {betrag} gehört es dir.').split('{betrag}');
      const satz = teile.map(escapeHtml).join('<b>' + escapeHtml(formatMoney(threshold, currency).replace(/\.00$/, '')) + '</b>');
      return (
        '<div class="mw-gs__karte mw-gs__karte--zu" role="listitem" data-mamm-goodie-key="' + key + '">' +
        '<div class="mw-gs__bild">' + imgHtml +
        '<span class="mw-gs__schloss" aria-hidden="true"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg></span>' +
        '</div>' +
        '<div class="mw-gs__text">' +
        '<b class="mw-gs__name">' + titleEsc + ' ' + escapeHtml(l.lGratis || 'gratis') + '</b>' +
        '<span class="mw-gs__satz">' + satz + '</span>' +
        '</div>' +
        '</div>'
      );
    }

    // Freigeschaltet: oranger Rahmen, Marke «Gratis», Satz, Knopf
    const knopf = l.lKnopf || 'In den Warenkorb';
    return (
      '<div class="mw-gs__karte mw-gs__karte--frei" role="listitem" data-mamm-goodie-key="' + key + '">' +
      '<div class="mw-gs__bild">' + imgHtml + '</div>' +
      // Aufbau wie «Passt dazu» (Wunsch Albinot 17.10.2026): Marke und Name links,
      // rechts oben «Gratis» an der Stelle des Preises, darunter derselbe Knopf
      '<div class="mw-gs__text">' +
      '<span class="mw-gs__hersteller">AntiDirt</span>' +
      '<b class="mw-gs__name">' + titleEsc + '</b>' +
      '<span class="mw-gs__satz">' + escapeHtml(l.lSatz || '') + '</span>' +
      '</div>' +
      '<div class="mw-gs__rechts">' +
      '<span class="mw-gs__gratis">' + escapeHtml(l.lMarke || 'Gratis') + '</span>' +
      '<button type="button" class="mw-fd__knopf mw-gs__knopf" data-mamm-goodie-add' +
      ' data-mamm-promotion-id="' + escapeAttr(promotion.id) + '"' +
      ' data-mamm-goodie-id="' + escapeAttr(goodie.id) + '"' +
      ' aria-label="' + escapeAttr(knopf + ' — ' + title) + '">' +
      escapeHtml(knopf) +
      '</button>' +
      '</div>' +
      '</div>'
    );
  }

  async function onGoodieAddClick(e) {
    const btn = e.currentTarget;
    if (btn.classList.contains('mw-fd__knopf--laedt')) return;

    btn.classList.add('mw-fd__knopf--laedt');
    btn.setAttribute('disabled', '');

    const itemEl = btn.closest('.mw-gs__karte, .mw-fd__karte');

    // Premium-Slide-Out: Höhe capturen, force reflow, dann Klasse setzen.
    // Item kollabiert smooth (max-height + padding + opacity), nutzt CSS-transitions.
    if (itemEl) {
      const h = itemEl.offsetHeight;
      itemEl.style.maxHeight = h + 'px';
      // eslint-disable-next-line no-unused-expressions
      itemEl.offsetHeight; // force reflow so transition picks up captured height
      itemEl.classList.add('mw-fd__karte--weg');
    }

    try {
      const promotionId = btn.getAttribute('data-mamm-promotion-id');
      const goodieId = btn.getAttribute('data-mamm-goodie-id');
      let promotion = null;
      let goodie = null;
      for (let i = 0; i < state.promotions.length; i++) {
        if (String(state.promotions[i].id) === String(promotionId)) {
          promotion = state.promotions[i];
          break;
        }
      }
      if (!promotion) {
        if (itemEl) {
          itemEl.classList.remove('mw-fd__karte--weg');
          itemEl.style.maxHeight = '';
        }
        return;
      }
      const goodies = Array.isArray(promotion.goodies) ? promotion.goodies : [];
      for (let j = 0; j < goodies.length; j++) {
        if (String(goodies[j].id) === String(goodieId)) {
          goodie = goodies[j];
          break;
        }
      }
      if (!goodie) {
        if (itemEl) {
          itemEl.classList.remove('mw-fd__karte--weg');
          itemEl.style.maxHeight = '';
        }
        return;
      }

      // Re-check eligibility (defense-in-depth)
      const freshCart = await fetchCart();
      if (freshCart) state.cart = freshCart;
      if (!isGoodieEligible(goodie, state.cart, promotion)) {
        // Cart fell below threshold — restore item visually, skip add
        if (itemEl) {
          itemEl.classList.remove('mw-fd__karte--weg');
          itemEl.style.maxHeight = '';
        }
        await sync();
        return;
      }

      const key = goodieKey(promotion, goodie);
      if (state.removedKeys.has(key)) {
        state.removedKeys.delete(key);
        persistRemoved();
      }

      // Parallel: addGoodie (server) + Animation (client, ~380ms).
      // addGoodie returnt { items, sections } — Sections werden NACH der
      // Animation dispatched, damit Pebble's morph nicht das gerade animierte
      // Item zerstört.
      const results = await Promise.all([
        addGoodie(promotion, goodie),
        new Promise(function (r) { setTimeout(r, 380); }),
      ]);
      const addResponse = results[0];

      // State aktualisieren BEVOR wir Pebble's morph triggern, damit unser
      // sofort-render (durch MutationObserver nach morph) bereits den frischen
      // Cart-State kennt.
      const newCart = await fetchCart();
      if (newCart) state.cart = newCart;

      // Jetzt: Pebble's CartUpdate-Event mit sections → morphSection rendert
      // das Goodie smooth im Cart-Items-Bereich, ohne Page-Reload.
      if (addResponse && addResponse.sections) {
        dispatchCartUpdate(addResponse.sections, newCart ? newCart.item_count : undefined);
      }

      // sync() macht final Consistency-Check + rendert Goodies-Section
      // (added Goodie ist nicht mehr in goodiesNotInCart → fällt aus Liste)
      await sync();
    } catch (err) {
      console.warn('[mamm-gift-engine] addGoodie click failed:', err);
      if (itemEl) {
        itemEl.classList.remove('mw-fd__karte--weg');
        itemEl.style.maxHeight = '';
      }
      btn.classList.remove('mw-fd__knopf--laedt');
      btn.removeAttribute('disabled');
    }
  }

  // ─────────────────────────────────────────────────────────
  //  Init
  // ─────────────────────────────────────────────────────────
  const debouncedSync = debounce(sync, CART_FETCH_DEBOUNCE_MS);
  const debouncedDecorate = debounce(function () {
    decorateCartItems();
    updateProgressBar();
    renderGoodiesSection();
  }, 80);

  function init() {
    sync();

    // Pebble's eigene Event-Konvention ist "cart:update" (CartUpdateEvent +
    // CartAddEvent verwenden beide diesen Namen). Plus historische Aliasse für
    // andere Apps/Themes.
    const cartEvents = ['cart:update', 'cart:updated', 'cart:change', 'product:added', 'ajaxCart:afterUpdate'];
    cartEvents.forEach(function (ev) {
      document.addEventListener(ev, function (e) {
        // Eigene Dispatches ausfiltern (zwei mögliche Source-Pfade in detail —
        // Pebble nutzt detail.data.source, andere Libs detail.source)
        if (e && e.detail && e.detail.source === 'mamm-gift-engine') return;
        if (e && e.detail && e.detail.data && e.detail.data.source === 'mamm-gift-engine') return;
        debouncedSync();
      });
    });

    // MutationObserver gezielt auf alle cart-items-components hängen — NICHT
    // auf body. Body-Observer würde bei jeder DOM-Mutation in der ganzen Seite
    // feuern (z.B. PDP-Variant-Wechsel, Carousel-Slides) → Performance-Killer.
    // Pebble's morphSection ersetzt cart-items-component innerHTML — damit
    // bekommen wir alle relevanten Cart-Re-Renders.
    const observerTargets = Array.from(
      document.querySelectorAll('cart-items-component[data-section-id]')
    );
    if (observerTargets.length === 0) {
      // Fallback: Cart-Drawer-Container falls cart-items-component noch nicht
      // im DOM (z.B. wenn Drawer lazy-rendered wird)
      const drawer =
        document.querySelector('cart-drawer-component') ||
        document.querySelector('#CartDrawer');
      if (drawer) observerTargets.push(drawer);
    }

    if (observerTargets.length > 0) {
      // Premium-Lösung: SOFORT mit cached state.cart re-rendern, sodass UI
      // nahtlos bleibt. debouncedSync läuft im Hintergrund + korrigiert
      // eventuelle Cache-Drift gegen Server-Truth.
      const observer = new MutationObserver(function (mutations) {
        let relevant = false;
        for (let i = 0; i < mutations.length; i++) {
          const m = mutations[i];
          // Eigene DOM-Mutationen (Goodies-Section + Badge-Inserts) ignorieren
          if (m.target && m.target.closest && m.target.closest('[data-mamm-gift-goodies], .mamm-gift-progress, .mamm-gift-badge')) {
            continue;
          }
          if (m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0)) {
            relevant = true;
            break;
          }
        }
        if (relevant) {
          if (state.cart) {
            decorateCartItems();
            updateProgressBar();
            renderGoodiesSection();
          }
          debouncedSync();
        }
      });
      observerTargets.forEach(function (target) {
        observer.observe(target, { childList: true, subtree: true });
      });
    }

    // Cart-Page Detection: pathname endet mit /cart (robust gegen Markets-
    // Prefixe wie /de-de/cart, /fr/cart etc.).
    if (location.pathname.endsWith('/cart')) {
      debouncedSync();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─────────────────────────────────────────────────────────
  //  Public API
  // ─────────────────────────────────────────────────────────
  window.MammGiftEngine = {
    sync: sync,
    state: state,
    config: config,
    clearRemoved: function () {
      state.removedKeys.clear();
      persistRemoved();
      sync();
    },
    version: '2.0.0',
  };
})();
